import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/app/config/environments/environment';
import { RoomDto } from '../models/dto/room.dto';
import { UserMessageDto } from '../models/dto/user-message.dto';
import {
  LastReadMessage,
  UserPreferences,
} from '../models/dto/user-preferences.dto';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private _path = 'user-preferences';

  private archivedRoomsKey = 'archivedRooms';
  private archivedUserChatsKey = 'archivedUserChats';
  private _archivedRooms: string[];
  private _archivedUserChats: string[];

  // unread read controls settings
  private unreadRoomsKey = 'unreadRooms';
  private _unreadRooms: string[];
  private unreadUserChatsKey = 'unreadUserChats';
  private _unreadUserChats: string[];

  private messagesByChatKey = 'messagesByChatKey';
  private _lastMessagesByChat: LastReadMessage[];

  constructor(private readonly http: HttpClient) {
    const archived = localStorage.getItem(this.archivedRoomsKey);
    this._archivedRooms = archived ? archived.split(',') : [];

    const archivedUser = localStorage.getItem(this.archivedUserChatsKey);
    this._archivedUserChats = archivedUser ? archivedUser.split(',') : [];

    const unreadRoom = localStorage.getItem(this.unreadRoomsKey);
    this._unreadRooms = unreadRoom ? unreadRoom.split(',') : [];

    const unreadUserMessage = localStorage.getItem(this.unreadUserChatsKey);
    this._unreadUserChats = unreadUserMessage
      ? unreadUserMessage.split(',')
      : [];

    const messagesByChat = localStorage.getItem(this.messagesByChatKey);
    this._lastMessagesByChat = messagesByChat ? JSON.parse(messagesByChat) : [];
  }

  config(userPreferences: UserPreferences): void {
    this._archivedRooms = userPreferences.rooms?.archived ?? [];
    this.saveArchivedRoomsOnStorage();

    this._archivedUserChats = userPreferences.userMessages?.archived ?? [];
    this.saveUserMessageOnStorage();

    this._unreadRooms = userPreferences.rooms?.unread ?? [];
    this.saveUnreadRoomOnStorage();

    this._unreadUserChats = userPreferences.userMessages?.unread ?? [];
    this.saveUnreadUserMessagesOnStorage();

    this._lastMessagesByChat = userPreferences.messagesMarkedAsRead ?? [];
    this.saveLastMessagesOnStorage();
  }

  public get archivedRooms(): string[] {
    return this._archivedRooms;
  }

  public get archivedUserChats(): string[] {
    return this._archivedUserChats;
  }

  async archiveRoom(idRoom: string) {
    await this.http
      .put(
        `${environment.api.baseUrl}/${this._path}/archive-room/${idRoom}`,
        {}
      )
      .toPromise();
    this._archivedRooms.push(idRoom);
    this.saveArchivedRoomsOnStorage();
  }

  async unarchiveRoom(idRoom: string) {
    await this.http
      .put(
        `${environment.api.baseUrl}/${this._path}/unarchive-room/${idRoom}`,
        {}
      )
      .toPromise();
    this._archivedRooms = this._archivedRooms.filter((r) => r !== idRoom);
    this.saveArchivedRoomsOnStorage();
  }

  async archiveUserChat(idUser: string) {
    await this.http
      .put(
        `${environment.api.baseUrl}/${this._path}/archive-user-message/${idUser}`,
        {}
      )
      .toPromise();
    this._archivedUserChats.push(idUser);
    this.saveUserMessageOnStorage();
  }

  async unarchiveUserChat(idUser: string) {
    await this.http
      .put(
        `${environment.api.baseUrl}/${this._path}/unarchive-user-message/${idUser}`,
        {}
      )
      .toPromise();
    this._archivedUserChats = this._archivedUserChats.filter(
      (r) => r !== idUser
    );
    this.saveUserMessageOnStorage();
  }

  private saveArchivedRoomsOnStorage() {
    localStorage.setItem(this.archivedRoomsKey, this._archivedRooms.join(','));
  }

  private saveUserMessageOnStorage() {
    localStorage.setItem(
      this.archivedUserChatsKey,
      this._archivedUserChats.join(',')
    );
  }

  //#region read/unread
  private saveUnreadRoomOnStorage() {
    localStorage.setItem(this.unreadRoomsKey, this._unreadRooms.join(','));
  }

  private saveUnreadUserMessagesOnStorage() {
    localStorage.setItem(
      this.unreadUserChatsKey,
      this._unreadUserChats.join(',')
    );
  }

  private saveLastMessagesOnStorage() {
    if (this._lastMessagesByChat) {
      localStorage.setItem(
        this.messagesByChatKey,
        JSON.stringify(this._lastMessagesByChat)
      );
    }
  }

  public get unreadRooms(): string[] {
    return this._unreadRooms;
  }

  async markRoomAsUnread(idRoom: string): Promise<void> {
    await this.http
      .patch(
        `${environment.api.baseUrl}/${this._path}/mark-room-unread/${idRoom}`,
        {}
      )
      .toPromise();

    this._lastMessagesByChat = this._lastMessagesByChat.filter(
      (message) => message.idChat !== idRoom
    );
    this._unreadRooms.push(idRoom);

    this.saveUnreadRoomOnStorage();
    this.saveLastMessagesOnStorage();
  }

  async markRoomAsRead(room: RoomDto): Promise<void> {
    await this.http
      .patch(`${environment.api.baseUrl}/${this._path}/mark-room-read`, {
        idChat: room.id,
        idLastMessage: room.numMessagesUnread > 0 ? room.lastMessage._id : null,
      })
      .toPromise();

    if (room.numMessagesUnread > 0) {
      this._lastMessagesByChat.push({
        idChat: room.id,
        idMessage: room.lastMessage._id || '',
      });
    }
    this._unreadRooms = this._unreadRooms.filter((r) => r !== room.id);

    this.saveUnreadRoomOnStorage();
    this.saveLastMessagesOnStorage();
  }

  public get unreadUserChats(): string[] {
    return this._unreadUserChats;
  }

  async markUserChatAsUnread(idUser: string): Promise<void> {
    await this.http
      .patch(
        `${environment.api.baseUrl}/${this._path}/mark-user-message-unread/${idUser}`,
        {}
      )
      .toPromise();

    this._lastMessagesByChat = this._lastMessagesByChat.filter(
      (message) => message.idChat !== idUser
    );
    this._unreadUserChats.push(idUser);

    this.saveUnreadUserMessagesOnStorage();
    this.saveLastMessagesOnStorage();
  }

  async markUserChatAsRead(userMessage: UserMessageDto) {
    await this.http
      .patch(
        `${environment.api.baseUrl}/${this._path}/mark-user-message-read`,
        {
          idChat: userMessage.user.id,
          idLastMessage:
            userMessage.numMessagesUnread > 0
              ? userMessage.lastMessage.id
              : null,
        }
      )
      .toPromise();

    if (userMessage.numMessagesUnread > 0) {
      this._lastMessagesByChat.push({
        idChat: userMessage.user.id,
        idMessage: userMessage.lastMessage.id || '',
      });
    }

    this._unreadUserChats = this._unreadUserChats.filter(
      (r) => r !== userMessage.user.id
    );

    this.saveUnreadUserMessagesOnStorage();
    this.saveLastMessagesOnStorage();
  }

  getLastMessageId(idChat: string): string | undefined {
    return this._lastMessagesByChat.find((val) => val.idChat === idChat)
      ?.idMessage;
  }
  //#endregion

  clear() {
    localStorage.removeItem(this.archivedUserChatsKey);
    localStorage.removeItem(this.archivedRoomsKey);

    localStorage.removeItem(this.unreadRoomsKey);
    localStorage.removeItem(this.unreadUserChatsKey);
    localStorage.removeItem(this.messagesByChatKey);
  }
}
